import React, { useCallback, useEffect, useState } from "react";
import API from "../services/api";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import RowActions from "../components/RowActions";
import {
  ClearOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import CustomForm from "../components/CustomForm";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import GeneratePaymentLinkModal from "../components/GeneratePaymentLinkModal";
import { useAuth } from "../context/AuthContext";
import { canWrite } from "../constants/permissions";
import {
  buildAmountBreakdown,
  canGeneratePaymentLink,
  describeEmailStatus,
  describeQrCodeStatus,
  resolveQrCodeStatus,
} from "../utils/payments";
import {
  describeApiError,
  fetchPaymentProviderStatus,
  generatePaymentLink,
} from "../services/paymentsApi";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const formatDateForDisplay = (dateString) => formatDateForInput(dateString) || "N/A";

const prepareSubscriptionForEdit = (subscription) => ({
  ...subscription,
  start_date: formatDateForInput(subscription.start_date),
  end_date: formatDateForInput(subscription.end_date),
});

// The only fields the subscription API accepts on write. Built as an allowlist
// rather than by stripping known-bad keys, because the form is seeded from a
// whole API row: every read-only or derived field the backend adds to that row
// would otherwise be posted straight back, and the subscription validator
// rejects any key it does not recognize.
const WRITABLE_SUBSCRIPTION_FIELDS = [
  "customer_id",
  "plan_id",
  "start_date",
  "end_date",
  "status",
  "is_active",
  "isDeleted",
];

const buildSubscriptionPayload = (formData) =>
  WRITABLE_SUBSCRIPTION_FIELDS.reduce((payload, field) => {
    if (formData[field] !== undefined) {
      payload[field] = formData[field];
    }
    return payload;
  }, {});

// `expiry_state` is derived by the backend so the warning threshold has a single
// definition; the row colour and the badge both read it rather than recomputing
// "how close is this to expiring" in the browser.
const EXPIRY_ROW_CLASS = {
  expired: "subscription-row--expired",
  expiring_soon: "subscription-row--expiring",
};

const describeExpiry = (subscription) => {
  const daysRemaining = subscription?.days_until_end;

  if (subscription?.expiry_state === "expired") {
    // A row whose stored status still says Active while its end date has passed
    // is not a contradiction to explain away: the end date is what grants
    // access, so the tenant is already locked out and the scheduled job has yet
    // to catch up. Saying "Expired" beside an "Active" tag reads as a bug, so
    // the badge names the actual condition instead.
    return String(subscription?.status || "").toLowerCase() === "expired"
      ? { color: "red", label: "Expired" }
      : { color: "red", label: "Past end date" };
  }

  if (subscription?.expiry_state === "expiring_soon") {
    if (daysRemaining === 0) return { color: "orange", label: "Expires today" };
    if (daysRemaining === 1) return { color: "orange", label: "Expires tomorrow" };
    return { color: "orange", label: `Expires in ${daysRemaining} days` };
  }

  return null;
};

// Spelled exactly as the Subscriptions.status enum stores them -- "Expired" is
// capitalised and the rest are not. Shared by the edit form and the list filter
// so the two can never drift onto different spellings.
const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "Expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

const buildLookupOptions = (items, selectedItem) => {
  const options = (items || []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  if (selectedItem?.id && !options.some((option) => option.value === selectedItem.id)) {
    options.push({
      value: selectedItem.id,
      label: selectedItem.name || selectedItem.id,
    });
  }

  return options;
};

const resolveDisplayName = (record, relationKey, lookupList) =>
  record?.[relationKey]?.name ||
  lookupList.find((item) => item.id === record?.[`${relationKey}_id`])?.name ||
  "N/A";

const formatSubscriptionStatus = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "";

const SubscriptionManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Mirrors routes/paymentRoutes.js, which is where it is actually enforced.
  const mayManagePayments = canWrite(user, "payments");

  // Payment link generation, kept apart from the subscription form state so a
  // payment in flight cannot interfere with an edit.
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [paymentSubscription, setPaymentSubscription] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  // Empty key means "no column sort asked for", which is what makes the backend
  // fall back to its expiry ordering: whatever is closest to lapsing leads the
  // list. Clicking a column header replaces that until the sort is cleared.
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchInitialData = useCallback(async () => {
    try {
      const [customersRes, plansRes] = await Promise.all([API.get("/customers"), API.get("/plans")]);
      setCustomers((customersRes.data.data || []).filter((customer) => customer.is_active));
      setPlans((plansRes.data.data || []).filter((plan) => plan.is_active));
    } catch (error) {
      enqueueSnackbar("Error fetching initial data", { variant: "error" });
    }
  }, [enqueueSnackbar]);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearchTerm,
        status: statusFilter,
        plan_id: planFilter,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };
      const response = await API.get("/subscriptions", { params });
      setSubscriptions(response.data.data || []);
      setTotalCount(
        response.data.meta && typeof response.data.meta.total === "number"
          ? response.data.meta.total
          : Array.isArray(response.data.data)
            ? response.data.data.length
            : 0
      );
    } catch (error) {
      enqueueSnackbar("Error fetching subscriptions", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearchTerm,
    statusFilter,
    planFilter,
    sortConfig.key,
    sortConfig.direction,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Asked once. On a deployment with no Razorpay credentials the action is
  // hidden rather than offered as a button that can only fail.
  useEffect(() => {
    if (!mayManagePayments) return;

    let cancelled = false;
    fetchPaymentProviderStatus()
      .then((response) => {
        if (!cancelled) setPaymentProvider(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setPaymentProvider({ configured: false });
      });

    return () => {
      cancelled = true;
    };
  }, [mayManagePayments]);

  const openPaymentModal = (subscription) => {
    setPaymentSubscription(subscription);
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    if (paymentSubmitting) return;
    setPaymentModalOpen(false);
    setPaymentSubscription(null);
  };

  const handleGeneratePaymentLink = async () => {
    if (!paymentSubscription) return;

    setPaymentSubmitting(true);
    try {
      const response = await generatePaymentLink({
        customerId: paymentSubscription.customer_id,
        subscriptionId: paymentSubscription.id,
      });

      const result = response.data.data;
      setPaymentModalOpen(false);
      setPaymentSubscription(null);
      // Shown rather than announced in a toast: the link and the email outcome
      // are both things the admin may need to read, copy or act on.
      setPaymentResult(result);

      enqueueSnackbar(
        result.email?.sent
          ? "Payment link generated and emailed to the customer"
          : "Payment link generated, but the email could not be sent",
        { variant: result.email?.sent ? "success" : "warning" }
      );
    } catch (error) {
      const described = describeApiError(error, "Could not generate the payment link");
      enqueueSnackbar(described.message, { variant: "error" });

      // A conflict carries the record that blocked it, so the admin is pointed
      // at the live link rather than left at a dead end.
      if (described.payment) {
        setPaymentModalOpen(false);
        setPaymentSubscription(null);
        setPaymentResult({ payment: described.payment, blocked: true, message: described.message });
      }
    } finally {
      setPaymentSubmitting(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Any change that narrows or reorders the list goes back to page 1, otherwise
  // a smaller result set can leave the table on a page that no longer exists.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, statusFilter, planFilter, sortConfig.key, sortConfig.direction]);

  const subscriptionFields = [
    {
      name: "customer_id",
      label: "Customer",
      type: "select",
      options: buildLookupOptions(customers, editingSubscription?.customer),
      required: true,
      width: 12,
    },
    {
      name: "plan_id",
      label: "Plan",
      type: "select",
      options: buildLookupOptions(plans, editingSubscription?.plan),
      required: true,
      width: 12,
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "date",
      required: true,
      width: 12,
    },
    {
      name: "end_date",
      label: "End Date",
      type: "date",
      required: true,
      width: 12,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: SUBSCRIPTION_STATUS_OPTIONS,
      required: true,
      width: 12,
    },
  ];

  const openCreateSubscription = () => {
    setEditingSubscription(null);
    setOpen(true);
  };

  const openEditSubscription = (subscription) => {
    setEditingSubscription(subscription);
    setOpen(true);
  };

  const closeSubscriptionForm = () => {
    setOpen(false);
    setEditingSubscription(null);
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    try {
      const payload = buildSubscriptionPayload(formData);
      if (editingSubscription) {
        await API.put(`/subscriptions/${editingSubscription.id}`, payload);
        enqueueSnackbar("Subscription updated successfully", { variant: "success" });
      } else {
        await API.post("/subscriptions", payload);
        enqueueSnackbar("Subscription created successfully", { variant: "success" });
      }
      fetchSubscriptions();
      closeSubscriptionForm();
    } catch (error) {
      // The validator reports rejected fields by name, so its message is more
      // use than a generic one.
      const validationErrors = error.response?.data?.errors;
      enqueueSnackbar(
        (Array.isArray(validationErrors) && validationErrors.join(" ")) ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Error saving subscription",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Customer",
      dataIndex: "customer_id",
      key: "customer_id",
      sorter: true,
      sortOrder: sortConfig.key === "customer_id" ? `${sortConfig.direction}end` : null,
      render: (_, subscription) => resolveDisplayName(subscription, "customer", customers),
    },
    {
      title: "Plan",
      dataIndex: "plan_id",
      key: "plan_id",
      sorter: true,
      sortOrder: sortConfig.key === "plan_id" ? `${sortConfig.direction}end` : null,
      render: (_, subscription) => resolveDisplayName(subscription, "plan", plans),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      sorter: true,
      sortOrder: sortConfig.key === "start_date" ? `${sortConfig.direction}end` : null,
      render: (value) => formatDateForDisplay(value),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      sorter: true,
      sortOrder: sortConfig.key === "end_date" ? `${sortConfig.direction}end` : null,
      render: (value) => formatDateForDisplay(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: sortConfig.key === "status" ? `${sortConfig.direction}end` : null,
      render: (value, subscription) => {
        const color =
          value?.toLowerCase() === "active"
            ? "green"
            : value?.toLowerCase() === "inactive"
              ? "gold"
              : value?.toLowerCase() === "expired"
                ? "red"
                : "default";
        // A row that still reads "Active" can be two days from losing access,
        // which the status alone cannot say. The expiry badge carries that, and
        // is dropped once it would only repeat the status.
        const expiry = describeExpiry(subscription);

        return (
          <Space size={4} wrap>
            <Tag color={color}>{formatSubscriptionStatus(value)}</Tag>
            {expiry && expiry.label !== formatSubscriptionStatus(value) ? (
              <Tag color={expiry.color}>{expiry.label}</Tag>
            ) : null}
          </Space>
        );
      },
    },
    {
      // Whether the *current billing period* has been paid, not whether the
      // subscription itself is active. Distinct from Status above: a
      // subscription can be Active and still Pending payment (never billed
      // through this flow, or just renewed/expired and awaiting its next
      // bill), and this is what Generate Payment Link is actually gated on.
      title: "Payment",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (value) => (
        <Tag color={value === "paid" ? "green" : "gold"}>
          {value === "paid" ? "Paid" : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, subscription) => {
        // Only offered when the role may take payments and the environment can
        // actually create a link. Disabled with the reason when the
        // subscription itself is not billable, so the reader is not left
        // guessing why.
        const paymentEligibility = canGeneratePaymentLink(subscription);
        const showPaymentAction = mayManagePayments && paymentProvider?.configured;

        return (
        <RowActions
          disabled={loading}
          items={[
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => openEditSubscription(subscription),
            },
            showPaymentAction && {
              key: "payment-link",
              label: "Generate Payment Link",
              // The reason goes in a tooltip, not the label: a sentence in the
              // label stretches the whole dropdown to its width.
              tooltip: paymentEligibility.allowed ? null : paymentEligibility.reason,
              icon: <CreditCardOutlined />,
              disabled: !paymentEligibility.allowed,
              onClick: () => openPaymentModal(subscription),
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                setSubscriptionToDelete(subscription);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
        );
      },
    },
  ];

  const hasActiveFilters = Boolean(searchTerm || statusFilter || planFilter);

  // Only the plans the lookup returned can be offered, so a subscription on a
  // plan that has since been deactivated is reachable through search rather
  // than this dropdown.
  const planFilterOptions = plans.map((plan) => ({ value: plan.id, label: plan.name }));

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setPlanFilter("");
  };

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <ShoppingOutlined style={{ fontSize: 56, color: "rgba(100, 116, 139, 0.45)" }} />
      <Typography.Title level={4} style={{ marginTop: 16 }}>
        No subscriptions found
      </Typography.Title>
      <Typography.Text type="secondary">
        {hasActiveFilters
          ? "Clear the filters to see all subscriptions."
          : "Add the first subscription to get started."}
      </Typography.Text>
      <div style={{ marginTop: 20 }}>
        {hasActiveFilters ? (
          <Button icon={<ClearOutlined />} onClick={clearFilters}>
            Clear filters
          </Button>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateSubscription}>
            Add subscription
          </Button>
        )}
      </div>
    </div>
  );

  const handleDeleteConfirm = async () => {
    if (!subscriptionToDelete) return;

    try {
      await API.delete(`/subscriptions/${subscriptionToDelete.id}`);
      enqueueSnackbar("Subscription deleted successfully", { variant: "success" });
      fetchSubscriptions();
    } catch (error) {
      enqueueSnackbar("Error deleting subscription", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setSubscriptionToDelete(null);
    }
  };

  return (
    <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 24 } }}>
      <div className="page-toolbar">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Subscription Management
          </Typography.Title>

        </div>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchSubscriptions} loading={loading} />
          {mayManagePayments && (
            <Button
              size="small"
              icon={<CreditCardOutlined />}
              onClick={() => navigate("/payments")}
            >
              Payment History
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateSubscription}
            disabled={loading}
          >
            Add Subscription
          </Button>
        </Space>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search subscriptions..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          style={{ width: 300 }}
          disabled={loading}
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 200 }}
          placeholder="Status"
          options={[{ value: "", label: "All Statuses" }, ...SUBSCRIPTION_STATUS_OPTIONS]}
        />
        <Select
          value={planFilter}
          onChange={setPlanFilter}
          style={{ width: 240 }}
          placeholder="Plan"
          showSearch
          optionFilterProp="label"
          options={[{ value: "", label: "All Plans" }, ...planFilterOptions]}
        />
        {hasActiveFilters && (
          <Button icon={<ClearOutlined />} onClick={clearFilters}>
            Clear
          </Button>
        )}
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={subscriptions}
        loading={loading}
        rowClassName={(subscription) => EXPIRY_ROW_CLASS[subscription?.expiry_state] || ""}
        locale={{ emptyText: emptyState }}
        pagination={{
          current: page + 1,
          pageSize: rowsPerPage,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 25],
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage - 1);
            setRowsPerPage(nextPageSize);
          },
        }}
        onChange={(_, __, sorter) => {
          // A third click on a header clears the sort: antd still reports the
          // field but drops `order`. Treating that as "ascending" would make
          // the sort impossible to switch off, so it goes back to the default
          // expiry ordering instead.
          if (!sorter?.field || !sorter?.order) {
            setSortConfig({ key: "", direction: "" });
            return;
          }

          setSortConfig({
            key: sorter.field,
            direction: sorter.order === "descend" ? "desc" : "asc",
          });
        }}
      />

      <Modal
        open={open}
        destroyOnHidden
        title={
          <div
            style={{
              width: "100%",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            {editingSubscription ? "Edit Subscription" : "Add New Subscription"}
          </div>
        }
        onCancel={() => {
          if (!loading) {
            closeSubscriptionForm();
          }
        }}
        footer={null}
        width={760}
      >
        <CustomForm
          fields={subscriptionFields}
          initialValues={
            editingSubscription
              ? prepareSubscriptionForEdit(editingSubscription)
              : {}
          }
          formResetKey={editingSubscription?.id || "create"}
          onSubmit={handleFormSubmit}
          loading={loading}
          buttonText={
            editingSubscription
              ? "Update Subscription"
              : "Create Subscription"
          }
          onCancel={closeSubscriptionForm}
        />
      </Modal>

      <Modal
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSubscriptionToDelete(null);
        }}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true, loading }}
        title="Confirm delete"
      >
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text>
            Are you sure you want to delete this subscription?
          </Typography.Text>
          {subscriptionToDelete && (
            <>
              <Typography.Text>
                Customer: {resolveDisplayName(subscriptionToDelete, "customer", customers)}
              </Typography.Text>
              <Typography.Text>
                Plan: {resolveDisplayName(subscriptionToDelete, "plan", plans)}
              </Typography.Text>
              <Typography.Text>
                Status:{" "}
                <Tag>{formatSubscriptionStatus(subscriptionToDelete.status)}</Tag>
              </Typography.Text>
            </>
          )}
        </Space>
      </Modal>

      <GeneratePaymentLinkModal
        open={paymentModalOpen}
        subscription={paymentSubscription}
        customers={customers}
        plans={plans}
        linkExpiryMinutes={paymentProvider?.linkExpiryMinutes}
        loading={paymentSubmitting}
        onCancel={closePaymentModal}
        onConfirm={handleGeneratePaymentLink}
      />

      <Modal
        open={Boolean(paymentResult)}
        title={paymentResult?.blocked ? "Payment link not generated" : "Payment link generated"}
        onCancel={() => setPaymentResult(null)}
        footer={
          <Space>
            <Button onClick={() => setPaymentResult(null)}>Close</Button>
            <Button
              type="primary"
              onClick={() => {
                setPaymentResult(null);
                navigate("/payments");
              }}
            >
              View payment history
            </Button>
          </Space>
        }
        width={620}
      >
        {paymentResult && (
          <Space orientation="vertical" size={14} style={{ width: "100%" }}>
            {paymentResult.blocked ? (
              <Alert type="warning" showIcon message={paymentResult.message} />
            ) : paymentResult.email?.sent ? (
              <Alert
                type="success"
                showIcon
                message="A payment link and QR code were emailed to the customer"
                description="The subscription will be renewed automatically once a payment is verified through either one."
              />
            ) : (
              /* The link (and QR, if created) exist and are payable; only the
                 email failed. Shown as a separate, retryable problem rather
                 than as a failed payment, and the link is offered for copying
                 so it can be sent by hand. */
              <Alert
                type="warning"
                showIcon
                message="The payment link was created, but the email could not be sent"
                description={
                  <>
                    {paymentResult.email?.error ? `${paymentResult.email.error}. ` : ""}
                    Use Resend Payment Link from Payment History, or send the link below manually.
                    Do not generate a new link — this one is still valid.
                  </>
                }
              />
            )}

            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                {
                  key: "reference",
                  label: "Payment reference",
                  children: (
                    <Typography.Text copyable style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 12 }}>
                      {paymentResult.payment?.reference_id}
                    </Typography.Text>
                  ),
                },
                ...buildAmountBreakdown(paymentResult.payment).map((row, index) => ({
                  key: `amount-${index}`,
                  label: row.label,
                  children: row.strong ? <Typography.Text strong>{row.value}</Typography.Text> : row.value,
                })),
                ...(paymentResult.payment?.payment_link_url
                  ? [
                      {
                        key: "link",
                        label: "Payment link",
                        children: (
                          <Typography.Text copyable={{ text: paymentResult.payment.payment_link_url }}>
                            <Typography.Link
                              href={paymentResult.payment.payment_link_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open hosted payment page
                            </Typography.Link>
                          </Typography.Text>
                        ),
                      },
                    ]
                  : []),
                ...(paymentResult.payment?.razorpay_qr_code_id
                  ? [
                      {
                        key: "qr",
                        label: "QR code (UPI)",
                        children: paymentResult.payment.qr_code_image_url ? (
                          <Space size={8} wrap>
                            <Typography.Link
                              href={paymentResult.payment.qr_code_image_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open QR image
                            </Typography.Link>
                            <Tag color={describeQrCodeStatus(resolveQrCodeStatus(paymentResult.payment)).color}>
                              Valid up to 2 hours
                            </Tag>
                          </Space>
                        ) : (
                          <Typography.Text type="secondary">
                            Could not be created — the payment link above still works.
                          </Typography.Text>
                        ),
                      },
                    ]
                  : []),
                {
                  key: "email-status",
                  label: "Email status",
                  children: (() => {
                    const meta = describeEmailStatus(
                      paymentResult.payment?.payment_link_email_status
                    );
                    return <Tag color={meta.color}>{meta.label}</Tag>;
                  })(),
                },
              ]}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default SubscriptionManagement;
